import { Button, Dialog, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AutocompleteCreatable from "@entrilia/shared/components/AutocompleteCreatable";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../../api/adminApi";
import type { FundraisingAccessItem } from "../../../../../../api/types/fundraisingTypes";
import type { AccessValue } from "../../../../../common/AccessEditor";
import AccessEditor from "../../../../../common/AccessEditor";
import type { SelectOption } from "../../../../../common/filters/filterTypes";
import MultiselectAutocomplete from "../../../../../common/MultiselectAutocomplete";
import AddContactDialog from "../../../../fund-structure/common/AddContactDialog";
import useFundraisingDetailsPageContext from "../useFundraisingDetailsPageContext";
import CreateInvestorDialog from "./CreateInvestorDialog";

interface Props {
  currentAccessAssignments: FundraisingAccessItem[];
  fundId: string;
  onSubmit: () => void;
  onClose: () => void;
}

interface InvestorAccessForm {
  selectedInvestorOption: InvestorOption | null;
  selectedContactOptions: ContactOption[];
}

interface InvestorOption {
  value: string;
  label: string;
  isNew?: boolean;
}

interface ContactOption {
  value: string;
  label: string;
  email: string;
  isNew?: boolean;
}

interface ChildDialogState {
  openDialog?: "create_investor" | "create_contact";
}

const isFormValid = (form: InvestorAccessForm, accessValue: AccessValue) =>
  form.selectedInvestorOption !== null &&
  form.selectedContactOptions.length > 0 &&
  accessValue.selectedCategoryIds.length > 0;

const createFundInvestor = withErrorHandling(adminApi.createFundInvestor);
const updateFundInvestor = withErrorHandling(adminApi.updateFundInvestor);

const AddFundraisingAccessDialog = ({ currentAccessAssignments, fundId, onSubmit, onClose }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { fundraisingCategories } = useFundraisingDetailsPageContext();

  const getFundPermissions = useCallback(async () => adminApi.getFundPermissions(fundId), [fundId]);

  const [fundPermissions, fundPermissionsError, { isFetching: isFetchingFundPermissions }] =
    useFetch(getFundPermissions);

  const getInvestors = useCallback(
    async () => adminApi.searchInvestors({ fieldIds: [], includeFunds: false, includeContacts: false }),
    []
  );

  const [investors, investorsError, { isFetching: isFetchingInvestors }] = useFetch(getInvestors);

  const getContacts = useCallback(
    async () => adminApi.searchContacts({ fieldIds: [], includeFundInvestors: false, includeInboxData: false }),
    []
  );

  const [contacts, contactsError, { isFetching: isFetchingContacts }] = useFetch(getContacts);

  const [investorAccessForm, setInvestorAccessForm] = useState<InvestorAccessForm>({
    selectedInvestorOption: null,
    selectedContactOptions: [],
  });

  const [accessValue, setAccessValue] = useState<AccessValue>({
    selectedRole: "LP_Member",
    selectedCategoryIds: fundraisingCategories.map((c) => c.externalId),
  });

  const [isSaving, setIsSaving] = useState(false);
  const [childDialogState, setChildDialogState] = useState<ChildDialogState>({});

  const existingContactNames = useMemo(
    () =>
      new Set([
        ...(contacts?.items ?? []).map((c) => c.name),
        ...investorAccessForm.selectedContactOptions.map((o) => o.label),
      ]),
    [contacts?.items, investorAccessForm.selectedContactOptions]
  );

  const existingContactEmails = useMemo(
    () =>
      new Set([
        ...(contacts?.items ?? []).map((c) => c.email.toLowerCase()),
        ...investorAccessForm.selectedContactOptions.map((o) => o.email.toLowerCase()),
      ]),
    [contacts?.items, investorAccessForm.selectedContactOptions]
  );

  const isFetching = isFetchingFundPermissions || isFetchingInvestors || isFetchingContacts;
  const fetchError = fundPermissionsError || investorsError || contactsError;

  const investorOptions = useMemo(
    () =>
      (investors?.items ?? [])
        .map(({ id, title }) => ({ value: id, label: title }))
        .sort(stringComparerBy((o) => o.label)),
    [investors?.items]
  );

  const contactOptions = useMemo(() => {
    const selectedInvestorId = investorAccessForm.selectedInvestorOption?.value;

    const alreadyAssignedContactIds = selectedInvestorId
      ? currentAccessAssignments.filter((row) => row.investorId === selectedInvestorId).map((row) => row.contactId)
      : [];

    return (contacts?.items ?? [])
      .filter((c) => !alreadyAssignedContactIds.includes(c.id))
      .map(({ id, name, email }) => ({
        value: id,
        label: name,
        email,
      }))
      .sort(stringComparerBy((o) => o.label));
  }, [currentAccessAssignments, contacts?.items, investorAccessForm.selectedInvestorOption?.value]);

  const handleSave = async () => {
    if (fundPermissions === undefined) {
      return;
    }

    const { selectedInvestorOption } = investorAccessForm;
    if (selectedInvestorOption === null) {
      return;
    }

    const newContacts = investorAccessForm.selectedContactOptions
      .filter((o) => o.isNew)
      .map((o) => ({
        name: o.label,
        email: o.email,
        roles:
          accessValue.selectedRole === "None" || accessValue.selectedRole === undefined
            ? []
            : [accessValue.selectedRole],
        externalCategoryIds: accessValue.selectedCategoryIds,
      }));

    const existingContacts = investorAccessForm.selectedContactOptions
      .filter((o) => !o.isNew)
      .map((o) => ({
        contactId: o.value,
        roles:
          accessValue.selectedRole === "None" || accessValue.selectedRole === undefined
            ? []
            : [accessValue.selectedRole],
        externalCategoryIds: accessValue.selectedCategoryIds,
      }));

    const fundInvestorId = fundPermissions.permissions.find(
      (p) => p.investorId === selectedInvestorOption.value
    )?.fundInvestorId;

    if (fundInvestorId && !selectedInvestorOption.isNew) {
      setIsSaving(true);

      const [, error] = await updateFundInvestor(fundInvestorId, {
        newContacts,
        existingContacts,
        removedContactIds: [],
      });

      setIsSaving(false);

      if (error) {
        logError(error, "[AddFundraisingAccessDialog] updateFundInvestor");
        sendNotificationError(error.message);
        return;
      }

      onSubmit();
      onClose();
      return;
    }

    const investorId = selectedInvestorOption.isNew ? undefined : selectedInvestorOption.value;
    const newInvestorName = selectedInvestorOption.isNew ? selectedInvestorOption.label : undefined;

    setIsSaving(true);

    const [, error] = await createFundInvestor({
      fundId,
      investorId,
      newInvestorName,
      newContacts,
      existingContacts,
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[AddFundraisingAccessDialog] createFundInvestor");
      sendNotificationError(error.message);
      return;
    }

    onSubmit();
    onClose();
  };

  const handleAccessChange = (update: Partial<AccessValue>) => {
    setAccessValue((prev) => ({ ...prev, ...update }));
  };

  const handleInvestorSelected = (investorOption: SelectOption) => {
    setInvestorAccessForm((prev) => ({
      ...prev,
      selectedInvestorOption: investorOption,
      selectedContactOptions: [],
    }));
  };

  const handleContactsSelected = (contacts: ContactOption[]) => {
    setInvestorAccessForm((prev) => ({ ...prev, selectedContactOptions: contacts }));
  };

  const handleNewInvestorAdd = (name: string) => {
    setInvestorAccessForm((prev) => ({
      ...prev,
      selectedInvestorOption: { value: `_new_${name}`, label: name, isNew: true },
    }));
  };

  const handleNewContactAdd = (name: string, email: string) => {
    setInvestorAccessForm((prev) => ({
      ...prev,
      selectedContactOptions: [
        ...prev.selectedContactOptions,
        { value: `_new_${name}_${email}`, label: name, email: email, isNew: true },
      ],
    }));
  };

  const isValid = isFormValid(investorAccessForm, accessValue);

  const existingInvestorNames = (investors?.items ?? []).map((i) => i.title);

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Add New</DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Divider />

        <DialogContent>
          {isFetching && <InlineLoader />}
          {fetchError && <DataLoadingFailed title="Failed to load fund access data" />}
          {!isFetching && !fetchError && (
            <Stack spacing={4}>
              <Stack spacing={2}>
                <Typography color="textSecondary">Select an existing investor or create a new one.</Typography>
                <AutocompleteCreatable
                  withLazyRendering
                  noItemsText="No investors found"
                  onCreateOption={() => setChildDialogState({ openDialog: "create_investor" })}
                  openOnFocus
                  fullWidth
                  options={investorOptions}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  renderInput={(params) => <TextField {...params} placeholder="Investor" />}
                  value={investorAccessForm.selectedInvestorOption}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleInvestorSelected(newValue);
                    }
                  }}
                />
              </Stack>

              <Stack spacing={2}>
                <Typography color="textSecondary">
                  Select one or more existing contacts or create multiple new ones.
                </Typography>
                <MultiselectAutocomplete
                  options={contactOptions}
                  values={investorAccessForm.selectedContactOptions}
                  onSelected={handleContactsSelected}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  getOptionDescription={(option) => option.email}
                  placeholder="Contacts"
                  noItemsText="No contacts found"
                  onCreateOption={() => setChildDialogState({ openDialog: "create_contact" })}
                />
              </Stack>

              <AccessEditor
                value={accessValue}
                onChange={handleAccessChange}
                roleOptions={["LP_Member"]}
                categoryOptions={fundraisingCategories.map((c) => ({ id: c.externalId, label: c.name }))}
              />
            </Stack>
          )}
        </DialogContent>

        <Divider />
        <DialogButtons onCancel={onClose}>
          <Button
            variant="contained"
            loading={isSaving}
            onClick={handleSave}
            disabled={isFetching || !!fetchError || !isValid}
          >
            Add
          </Button>
        </DialogButtons>
      </Dialog>
      {childDialogState.openDialog === "create_investor" && (
        <CreateInvestorDialog
          onCreate={handleNewInvestorAdd}
          onClose={() => setChildDialogState({})}
          existingInvestorNames={existingInvestorNames}
        />
      )}
      {childDialogState.openDialog === "create_contact" && (
        <AddContactDialog
          mode="add"
          onAdd={handleNewContactAdd}
          onClose={() => setChildDialogState({})}
          existingContactNames={existingContactNames}
          existingContactEmails={existingContactEmails}
        />
      )}
    </>
  );
};

export default AddFundraisingAccessDialog;
